import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import attendikLogo from "../assets/attendik-logo.png";
import {
  CountsError,
  getStageCounts,
  type StageCounts
} from "../api/client.js";
import { AttendeesButton } from "../components/AttendeesButton.js";
import { SnapshotButton } from "../components/SnapshotButton.js";

const ACTIVE_INTERVAL_MS = 3000;
const HIDDEN_INTERVAL_MS = 10_000;

// Above this age, the indicator drops from "live" to "reconnecting" even if
// the last fetch happened to succeed — covers the case where the network
// stalls between polls. Mirrors BoardPage exactly.
const RECONNECTING_THRESHOLD_MS = 10_000;

// Capacity-driven colour thresholds, per CR-01 §3.3. Stage colour is the only
// signal here — actual alerting is the scan side's `STAGE_FULL` warning,
// which lives in `apps/scan`. The monitor just makes the state visible to
// the room.
const AMBER_RATIO = 0.8;
const RED_RATIO = 1.0;

interface DisplayState {
  counts: StageCounts | null;
  /** Status code of the most recent fetch failure (401, 404, …). */
  fatal: number | null;
  /** Wall-clock timestamp of the last successful poll. */
  lastUpdate: number | null;
}

/**
 * The live board for one stage (monitor CR-01). Same polling cadence and
 * error semantics as `BoardPage`, but bound to a stage_id rather than an
 * event_id and renders the stage-specific layout: `Event · Stage` title,
 * `currently_in / capacity` headline, two secondary counters (entries +
 * exits), capacity-driven colour on the headline.
 */
export function StageBoardPage(): JSX.Element {
  const { stageId } = useParams<{ stageId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [state, setState] = useState<DisplayState>({
    counts: null,
    fatal: null,
    lastUpdate: null
  });
  const [, force] = useState({});

  // Refs so the poll loop reads the current visibility / fatal state without
  // re-binding the interval every render.
  const fatalRef = useRef<number | null>(null);
  const pollRef = useRef<(() => void) | null>(null);

  const poll = useCallback(async (): Promise<void> => {
    if (!stageId || !token) {
      return;
    }
    if (fatalRef.current !== null) {
      return;
    }
    try {
      const counts = await getStageCounts(stageId, token);
      setState({ counts, fatal: null, lastUpdate: Date.now() });
    } catch (err) {
      if (
        err instanceof CountsError &&
        (err.status === 401 || err.status === 404)
      ) {
        fatalRef.current = err.status;
        setState((prev) => ({ ...prev, fatal: err.status }));
        return;
      }
      // Transient — keep the last successful counts on screen so the board
      // doesn't flash empty during a brief network blip.
      setState((prev) => ({ ...prev, lastUpdate: prev.lastUpdate }));
    }
  }, [stageId, token]);

  pollRef.current = poll;

  useEffect(() => {
    if (!stageId || !token) {
      return undefined;
    }
    void poll();

    let interval: ReturnType<typeof setInterval>;
    const arm = (): void => {
      clearInterval(interval);
      const ms =
        document.visibilityState === "hidden"
          ? HIDDEN_INTERVAL_MS
          : ACTIVE_INTERVAL_MS;
      interval = setInterval(() => {
        void (pollRef.current?.() ?? Promise.resolve());
      }, ms);
    };
    arm();

    const onVisibility = (): void => {
      arm();
      if (document.visibilityState !== "hidden") {
        void poll();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    // Tick the "X seconds ago" label once a second.
    const labelTimer = setInterval(() => force({}), 1000);

    return () => {
      clearInterval(interval);
      clearInterval(labelTimer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [stageId, token, poll]);

  if (!stageId || !token) {
    return <InvalidLinkView />;
  }
  if (state.fatal === 401) {
    return <InvalidLinkView />;
  }
  if (state.fatal === 404) {
    return <StageNotFoundView />;
  }
  if (!state.counts) {
    return <LoadingView />;
  }

  return (
    <StageBoard
      counts={state.counts}
      lastUpdate={state.lastUpdate}
      stageId={stageId}
      token={token}
    />
  );
}

// ---- Live board -------------------------------------------------------------

/**
 * Maps `currently_in / capacity` to the colour class for the headline number.
 * Returns white below the amber threshold; CR-01 §3.3 sets the cut-offs at
 * 80% and 100%. When `capacity` is null the colour is always white — there
 * is no scale to compare against.
 */
function headlineColourClass(
  currentlyIn: number,
  capacity: number | null
): string {
  if (capacity === null || capacity <= 0) {
    return "text-white";
  }
  const ratio = currentlyIn / capacity;
  if (ratio >= RED_RATIO) {
    return "text-red-400";
  }
  if (ratio >= AMBER_RATIO) {
    return "text-amber-300";
  }
  return "text-white";
}

function StageBoard({
  counts,
  lastUpdate,
  stageId,
  token
}: {
  counts: StageCounts;
  lastUpdate: number | null;
  stageId: string;
  token: string;
}): JSX.Element {
  const previous = usePrevious(counts);

  const pulseCurrentlyIn =
    previous && previous.currently_in !== counts.currently_in;

  const headlineColour = headlineColourClass(counts.currently_in, counts.capacity);

  return (
    <main className="flex h-full min-h-screen flex-col bg-slate-950 text-white">
      <header className="monitor-no-print flex items-center justify-between px-6 py-4 text-sm sm:px-10">
        <div className="flex min-w-0 items-center gap-4">
          <img
            src={attendikLogo}
            alt="Attendik"
            className="h-7 w-auto shrink-0 [filter:invert(1)]"
          />
          {/*
            Title format per CR-01 §3.1: "Event · Stage" with a centre-dot
            separator. data-testid lets tests assert the composite shape
            without coupling to specific event/stage strings.
          */}
          <h1
            data-testid="stage-title"
            className="truncate font-medium text-slate-200"
          >
            <span data-testid="event-name">{counts.event_name}</span>
            <span aria-hidden className="px-2 text-slate-500">
              ·
            </span>
            <span data-testid="stage-name">{counts.stage_name}</span>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <AttendeesButton eventId={counts.event_id} stageId={stageId} token={token} />
          <SnapshotButton eventId={counts.event_id} stageId={stageId} token={token} />
          <LiveIndicator lastUpdate={lastUpdate} />
        </div>
      </header>

      <section className="flex flex-1 flex-col items-center justify-center px-4 text-center">
        <div className="monitor-headline">
          <div className="flex items-end justify-center font-sans font-black leading-none tracking-tight">
            <span
              data-testid="currently-in"
              data-color={
                headlineColour === "text-red-400"
                  ? "red"
                  : headlineColour === "text-amber-300"
                    ? "amber"
                    : "white"
              }
              className={
                "block text-[clamp(3rem,22vw,18rem)] " +
                headlineColour +
                " " +
                (pulseCurrentlyIn ? "animate-pulse-pop" : "")
              }
            >
              {counts.currently_in}
            </span>
            {counts.capacity !== null ? (
              <span
                data-testid="capacity-denominator"
                className="ml-3 pb-[0.18em] text-[clamp(1.5rem,7vw,5rem)] font-bold text-slate-500"
              >
                / {counts.capacity}
              </span>
            ) : null}
          </div>
          <p className="mt-2 text-base uppercase tracking-[0.3em] text-slate-400 sm:text-lg">
            currently in stage
          </p>
        </div>
      </section>
    </main>
  );
}

function LiveIndicator({ lastUpdate }: { lastUpdate: number | null }): JSX.Element {
  const age = lastUpdate ? Date.now() - lastUpdate : null;
  const reconnecting = age === null || age > RECONNECTING_THRESHOLD_MS;
  if (reconnecting) {
    return (
      <span
        data-testid="indicator"
        data-status="reconnecting"
        className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-3 py-1 text-amber-300"
      >
        <span className="h-2 w-2 rounded-full bg-amber-400" aria-hidden />
        reconnecting…
      </span>
    );
  }
  const seconds = age === null ? 0 : Math.max(0, Math.floor(age / 1000));
  return (
    <span
      data-testid="indicator"
      data-status="live"
      className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-3 py-1 text-emerald-300"
    >
      <span className="h-2 w-2 rounded-full bg-emerald-400" aria-hidden />
      live · {seconds}s ago
    </span>
  );
}

// ---- States ----------------------------------------------------------------

function LoadingView(): JSX.Element {
  return (
    <main className="flex h-full min-h-screen items-center justify-center bg-slate-950 text-slate-400">
      <p className="text-sm uppercase tracking-[0.3em]">Loading…</p>
    </main>
  );
}

function InvalidLinkView(): JSX.Element {
  return (
    <main className="flex h-full min-h-screen flex-col items-center justify-center gap-3 bg-slate-950 px-6 text-center text-slate-200">
      <h2 className="text-2xl font-semibold">Invalid link</h2>
      <p className="max-w-md text-sm text-slate-400">
        This stage monitor link is no longer valid. Ask the event organiser
        to share an up-to-date link.
      </p>
    </main>
  );
}

function StageNotFoundView(): JSX.Element {
  return (
    <main className="flex h-full min-h-screen flex-col items-center justify-center gap-3 bg-slate-950 px-6 text-center text-slate-200">
      <h2 className="text-2xl font-semibold">Stage not found</h2>
      <p className="max-w-md text-sm text-slate-400">
        The stage for this link does not exist (or has been removed).
      </p>
    </main>
  );
}

// ---- helpers ---------------------------------------------------------------

function usePrevious<T>(value: T): T | null {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}
